import { useCallback } from 'react';
import styled from 'styled-components';
import { DateString, getDateOfDay, getDayOfWeek } from '../../../../common/src/date_string.ts';
import { useCurrentOrg } from '../../data/use_current_org.tsx';
import { useHousehold } from '../../data/use_household.tsx';
import { anySongInteractionsForDate, getStreakInDays } from '../../data/use_singing_plan_completions.ts';
import { useCurrentDate } from '../../util/use_current_date.tsx';
import { CheckMarkButton, Divider, Tile } from './shared.tsx';

export function HouseholdSingingPlanStreak({
  navigateToDate,
  isReviewTextVisible,
}: {
  navigateToDate: (date: DateString) => void;
  isReviewTextVisible: boolean;
}) {
  const { household } = useHousehold();
  const today = useCurrentDate();

  const message = isReviewTextVisible ? (
    <span>
      Click on a day to review.
      <br />
      Come back tomorrow to
      <br />
      continue your streak!
    </span>
  ) : (
    <span>
      Practicing daily increases
      <br />
      your confidence, missing
      <br />a day resets the streak!
    </span>
  );
  const streakInDays = getStreakInDays({ household, forDate: today });
  return (
    <Tile>
      <StreakStack>
        <Flame src="/images/flame.gif" />
        <StreakDays>{streakInDays} day streak</StreakDays>
        <DaysOfWeek navigateToDate={navigateToDate} />
        <Divider />
        <StreakMessage>{message}</StreakMessage>
      </StreakStack>
    </Tile>
  );
}

const StreakStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 10px;
  gap: 15px;
`;

const Flame = styled.img`
  margin-left: -10px;
  height: 150px; // 200px better on larger screens...
`;

const StreakDays = styled.div`
  font-family: Jost-SemiBold, 'Arial', sans-serif;
  font-size: 2.5em;
  color: var(--color-text-singing-plan-callout);
`;

const DaysOfWeek = ({ navigateToDate }: { navigateToDate: (date: DateString) => void }) => {
  const { household } = useHousehold();
  const { householdOrgs } = useCurrentOrg();
  const today = useCurrentDate();

  const getDayProperties = useCallback(
    (dayIndex: number) => {
      const date = getDateOfDay(dayIndex, today, { treatSundayAsLast: true });
      const completed = anySongInteractionsForDate(date, household);
      const isClickable =
        dayIndex <= getDayOfWeek(today) &&
        householdOrgs
          .flatMap(({ singingPlan }) => (singingPlan ? singingPlan.days.map((day) => day.date) : []))
          .includes(date);
      return { completed, date, isClickable };
    },
    [household, householdOrgs, today],
  );

  const days = [
    { abbreviation: 'Mo', ...getDayProperties(1) },
    { abbreviation: 'Tu', ...getDayProperties(2) },
    { abbreviation: 'We', ...getDayProperties(3) },
    { abbreviation: 'Th', ...getDayProperties(4) },
    { abbreviation: 'Fr', ...getDayProperties(5) },
    { abbreviation: 'Sa', ...getDayProperties(6) },
  ];

  const onDayClick = useCallback(
    (dateString: DateString) => {
      return () => navigateToDate(dateString);
    },
    [navigateToDate],
  );

  return (
    <DayList>
      {days.map(({ abbreviation, completed, date, isClickable }) => (
        <Day key={abbreviation} $completed={completed} onClick={isClickable ? onDayClick(date) : undefined}>
          <DayAbbreviation $completed={completed}>{abbreviation}</DayAbbreviation>
          <CheckMarkButton checked={completed} isPurple={true} />
        </Day>
      ))}
    </DayList>
  );
};

const DayList = styled.div`
  display: flex;
  gap: min(max(3.5vw, 10px), 20px);
`;

const Day = styled.div<{ $completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: ${(props) => (props.onClick ? 'pointer' : 'revert')};
`;

const DayAbbreviation = styled.div<{ $completed: boolean }>`
  font-family: Jost-SemiBold, 'Arial', sans-serif;
  font-size: 1.5em;
  color: ${(props) => (props.$completed ? 'var(--color-checkmark-song-purple)' : 'var(--color-text-lighter)')};
`;

const StreakMessage = styled.div`
  font-family: Jost-SemiBold, 'Arial', sans-serif;
  font-size: 1.2em;
  color: var(--color-text-darker);
  text-align: center;
  line-height: 1.3em;
  margin-top: -10px;
`;
